import type { Building } from "building-types/building/buildingTypes";
import type { BuildingEntity } from "building-types/entities/buildingEntity";
import { makeBuildingEntity } from "building-types/entities/buildingEntity";
import type { BuildingEntityHandle } from "building-types/entities/buildingEntityHandle";
import { isBuildingAnalyticEntityType } from "building-types/entities/buildingEntityTypeTypes";
import { buildingEntityTypeIsTableBased, entityTypeToTableName } from "./buildingTableBasedEntity";

export function buildingGetEntity(
  building: Building,
  handle: BuildingEntityHandle,
): Extract<BuildingEntity, { entityType: typeof handle.entityType }> | null {
  if (isBuildingAnalyticEntityType(handle.entityType)) {
    return building[entityTypeToTableName[handle.entityType]][handle.id] ?? null;
  }
  if (buildingEntityTypeIsTableBased(handle.entityType)) {
    const tableName = entityTypeToTableName[handle.entityType];
    const table = building[tableName];

    if (table) {
      const sourceEntity = table[handle.id];
      if (sourceEntity) {
        return makeBuildingEntity(handle, sourceEntity);
      }
    }

    return null;
  }

  switch (handle.entityType) {
    case "WALL_PLANE_SUBDOMAIN":
      const wallPlane = building.wallPlanes[handle.parent.id];
      if (!wallPlane) {
        return null;
      }
      const wallPlaneSubdomain = wallPlane.descriptor.subdomains[handle.indexV]?.[handle.indexU];
      if (!wallPlaneSubdomain) {
        return null;
      }
      return makeBuildingEntity(handle, wallPlaneSubdomain);
    case "INTERIOR_TRIM":
      const baseTrimMoldings = building.baseTrimMoldings;
      if (baseTrimMoldings && handle.id in baseTrimMoldings) {
        return makeBuildingEntity(handle, baseTrimMoldings[handle.id]);
      }
      const crownTrimMoldings = building.crownTrimMoldings;
      if (crownTrimMoldings && handle.id in crownTrimMoldings) {
        return makeBuildingEntity(handle, crownTrimMoldings[handle.id]);
      }
      return null;
    case "BUILDING_GRAPH_PLANAR_TOPOLOGY": {
      const topology = building.buildingGraph.faceTopologies[handle.id];
      if (!topology) {
        return null;
      }
      return makeBuildingEntity(handle, topology);
    }
    default:
      break;
  }

  // @TODO(Andrew): Handle non-table based entity types here
  return null;
}
